/**
 * JWT utility functions for parsing and validating tokens
 */

export interface JWTPayload {
  _id: string;
  email: string;
  role: string;
  accountId: string;
  fullName: string;
  type: 'access' | 'refresh';
  iat: number; // issued at
  exp: number; // expires at
}

/**
 * Parse JWT token without verification (client-side only)
 * Note: This is for reading expiry times only, not for security validation
 */
export function parseJWT(token: string): JWTPayload | null {
  try {
    if (!token) return null;
    
    // Split JWT into parts
    const parts = token.split('.');
    if (parts.length !== 3) {
      console.warn('Invalid JWT format');
      return null;
    }
    
    // Decode payload (middle part)
    const payload = parts[1];
    // Add padding if needed
    const paddedPayload = payload + '='.repeat((4 - payload.length % 4) % 4);
    const decodedPayload = atob(paddedPayload);
    
    return JSON.parse(decodedPayload) as JWTPayload;
  } catch (error) {
    console.warn('Failed to parse JWT:', error);
    return null;
  }
}

/**
 * Get token expiry time in milliseconds
 */
export function getTokenExpiry(token: string): number | null {
  const payload = parseJWT(token);
  return payload ? payload.exp * 1000 : null; // Convert to milliseconds
}

/**
 * Get time until token expires in milliseconds
 */
export function getTimeUntilExpiry(token: string): number | null {
  const expiry = getTokenExpiry(token);
  if (!expiry) return null;
  
  const now = Date.now();
  return Math.max(0, expiry - now);
}

/**
 * Check if token is expired
 */
export function isTokenExpired(token: string): boolean {
  const timeUntilExpiry = getTimeUntilExpiry(token);
  return timeUntilExpiry === null || timeUntilExpiry <= 0;
}

/**
 * Get token issue time in milliseconds
 */
export function getTokenIssueTime(token: string): number | null {
  const payload = parseJWT(token);
  return payload ? payload.iat * 1000 : null; // Convert to milliseconds
}

/**
 * Get access token from cookies
 */
export function getAccessTokenFromCookies(): string | null {
  const cookies = document.cookie.split(';');
  const accessTokenCookie = cookies.find(cookie => 
    cookie.trim().toLowerCase().startsWith('adminaccesstoken=')
  );
  
  if (!accessTokenCookie) return null;
  
  return accessTokenCookie.split('=')[1];
}

/**
 * Get refresh token from cookies
 */
export function getRefreshTokenFromCookies(): string | null {
  const cookies = document.cookie.split(';');
  const refreshTokenCookie = cookies.find(cookie => 
    cookie.trim().toLowerCase().startsWith('adminrefreshtoken=')
  );
  
  if (!refreshTokenCookie) return null;
  
  return refreshTokenCookie.split('=')[1];
}

/**
 * Get dynamic token lifetime based on actual JWT expiry
 */
export function getDynamicTokenLifetime(): number | null {
  const accessToken = getAccessTokenFromCookies();
  if (!accessToken) return null;
  
  const issueTime = getTokenIssueTime(accessToken);
  const expiryTime = getTokenExpiry(accessToken);
  
  if (!issueTime || !expiryTime) return null;
  
  return expiryTime - issueTime;
}
